import { ChevronDown, ChevronUp } from "@untitledui/icons";
import { useEffect, useRef, useState } from "react";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { cx } from "@/utils/cx";
import type { FilterOption } from "./model";

type Props = {
  label: string;
  options: FilterOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  searchable?: boolean;
  /** Which edge of the button the menu lines up with. A menu near the modal's right edge opens leftwards. */
  align?: "start" | "end";
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
};

export const FilterMenu = ({
  label,
  options,
  selected,
  onChange,
  searchable,
  align = "start",
  open,
  onOpen,
  onClose,
}: Props) => {
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) onClose();
    };
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [open, onClose]);

  const toggle = (value: string) =>
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);

  const needle = search.trim().toLowerCase();
  const visible = needle ? options.filter((option) => option.label.toLowerCase().includes(needle)) : options;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => (open ? onClose() : onOpen())}
        aria-expanded={open}
        className={cx(
          "inline-flex cursor-pointer items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-sm font-medium transition-colors",
          selected.length
            ? "border-purple-950 bg-purple-50 text-purple-950"
            : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50",
        )}
      >
        {label}
        {selected.length > 0 && (
          <span className="inline-flex min-w-[17px] items-center justify-center rounded-full bg-purple-950 px-1.5 text-xs font-semibold text-white">
            {selected.length}
          </span>
        )}
        {open ? (
          <ChevronUp className="size-3.5" aria-hidden="true" />
        ) : (
          <ChevronDown className="size-3.5" aria-hidden="true" />
        )}
      </button>
      {open && (
        <div
          className={cx(
            "absolute z-40 mt-1.5 w-[280px] rounded-xl border border-gray-200 bg-white p-2 shadow-lg",
            align === "end" ? "right-0" : "left-0",
          )}
        >
          {searchable && (
            <input
              // biome-ignore lint/a11y/noAutofocus: the menu opens on click and the search field is its only input
              autoFocus
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search…"
              aria-label={`Search ${label.toLowerCase()}s`}
              className="mb-1.5 w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm text-gray-900 outline-hidden focus:border-purple-950"
            />
          )}
          <div className="flex max-h-[260px] flex-col gap-0.5 overflow-y-auto">
            {visible.map((option) => (
              <div key={option.value} className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-gray-50">
                <Checkbox
                  className="min-w-0 flex-1 items-center"
                  isSelected={selected.includes(option.value)}
                  onChange={() => toggle(option.value)}
                  label={<span className="truncate">{option.label}</span>}
                />
                <span className="text-xs text-gray-400 tabular-nums">{option.count}</span>
              </div>
            ))}
            {visible.length === 0 && <p className="px-2 py-2 text-sm text-gray-500">No matches.</p>}
          </div>
          {selected.length > 0 && (
            <div className="mt-1.5 border-t border-gray-100 pt-1.5">
              <button
                type="button"
                onClick={() => onChange([])}
                className="cursor-pointer px-1 text-xs font-semibold text-purple-950"
              >
                Clear
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
